import React from 'react';
import { MapPin, Shield, Wrench, Users } from 'lucide-react';
import { aboutContent } from '../data/mock';

const AboutSection = () => {
  const features = [
    {
      icon: MapPin,
      title: "Perth Based",
      description: "Local manufacturing in Cannington, serving all of Western Australia"
    },
    {
      icon: Shield,
      title: "Quality Assured",
      description: "Every product tested to meet strict industry standards"
    },
    {
      icon: Wrench,
      title: "Custom Solutions",
      description: "Tailored paper cores and tubes to your exact specifications"
    },
    {
      icon: Users,
      title: "Industry Trusted",
      description: "Serving printing, packaging, and construction industries"
    }
  ];

  return (
    <section id="about" className="py-24 bg-neutral-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">About Us</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            {aboutContent.headline}
          </h2>
          <p className="mt-6 text-lg text-neutral-400 max-w-3xl mx-auto leading-relaxed">
            {aboutContent.description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-6 bg-neutral-900/50 border border-neutral-800 rounded-2xl hover:border-amber-400/50 transition-all duration-500"
            >
              <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-400/20 transition-colors">
                <feature.icon className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Values Section */}
        <div className="grid lg:grid-cols-3 gap-8">
          {aboutContent.values.map((value, index) => (
            <div
              key={index}
              className="relative p-8 bg-gradient-to-br from-neutral-900 to-neutral-950 border border-neutral-800 rounded-2xl overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/5 rounded-full blur-2xl group-hover:bg-amber-400/10 transition-colors" />
              <span className="text-6xl font-bold text-amber-400/10 absolute top-4 right-6">
                0{index + 1}
              </span>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{value.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* SEO Rich Content Block */}
        <div className="mt-20 p-8 bg-neutral-900/30 border border-neutral-800 rounded-2xl">
          <h3 className="text-2xl font-bold text-white mb-4">
            Your Paper Tube Supplier in Perth
          </h3>
          <div className="text-neutral-400 space-y-4 leading-relaxed">
            <p>
              As a leading <strong className="text-neutral-300">cardboard core manufacturer in Perth</strong>, 
              Adela Merchants specializes in producing high-quality paper tubes and cardboard cores for 
              industrial applications across Western Australia.
            </p>
            <p>
              Our product range includes <strong className="text-neutral-300">flexographic printing cores</strong>, 
              <strong className="text-neutral-300"> label roll cores</strong>, 
              <strong className="text-neutral-300"> stretch film cores</strong>, and 
              <strong className="text-neutral-300"> concrete form tubes</strong>. 
              We offer custom core diameters including 19mm, 25mm, 38mm, 40mm, 76mm, and 152mm cardboard cores.
            </p>
            <p>
              Whether you need <strong className="text-neutral-300">wholesale paper tubes in Perth</strong> or 
              custom heavy-duty cardboard cores for industrial winding, our team delivers precision-manufactured 
              products with fast turnaround times across WA.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
